import { ValidationLevel } from 'ui';
import { createFormatters } from 'app/infrastructure/data/date';
import { OptionItem } from 'app/components/display-selected-options/display-selected-options';
import { SearchFiltersTranslation } from '../constants';
import { FilterCommonTypes } from '../types-store';
import {
  PredefinedIds, FilterType, MapToSummaryPayload, MapCustomFieldPayload,
  OptionType, SubOptionType, FilterPayloadValues, FilterCommonValues, CustomFilterType
} from './types';
import { getFieldId } from './selectors';
import { createDateValue } from './create-date';
import { getCustomValue } from 'app/area/search-filters/filters/get-custom-value';
import { createNumberValue } from 'app/area/search-filters/filters/create-number';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { getExternalValue } from 'app/area/search-filters/filters/get-external-value';

interface FilterSummaryField {
  id: string;
  label?: string;
  values: (string | number)[];
  options: string[];
}

interface FilterSummary {
  title: string;
  fields: FilterSummaryField[];
}

const mapPredefinedFilters = (includeValues: FilterCommonValues<FilterCommonTypes>[] = [],
  excludeValues: FilterCommonValues<FilterCommonTypes>[] = [],
  translation: SearchFiltersTranslation): FilterSummary[] =>
  includeValues.length === 0 && excludeValues.length === 0
    ? []
    : [{
      title: translation.filters_predefined_filters,
      fields: [
        ...(includeValues.length > 0 ? [{
          id: 'predef_incl',
          values: includeValues.map(i => i && i.description || ''),
          options: []
        }] : []),
        ...(excludeValues.length > 0 ? [{
          id: 'predef_excl',
          values: excludeValues.map(i => i && i.description || ''),
          options: [translation.filter_summary_exclude]
        }] : [])
      ]
    }];

const maptCustomFieldValues = (
  id: string,
  filterType: FilterType,
  fieldType: CustomFilterType,
  values: FilterPayloadValues<FilterCommonTypes>,
  translation: SearchFiltersTranslation,
  locale: string
) => {
  const value = values[id];
  const formatters = createFormatters(locale);

  const dateFromValue = createDateValue(value, SubOptionType.dateFrom, formatters);
  const dateToValue = createDateValue(value, SubOptionType.dateTo, formatters);
  const notSpecified = value.some(f => f.optionType === OptionType.notSpecified);

  const decimalFromValue = createNumberValue(value, SubOptionType.decimalFrom, locale);
  const decimalToValue = createNumberValue(value, SubOptionType.decimalTo, locale);

  const isDecimalFrom = isDefinedIncludingZero(decimalFromValue);
  const isDecimalTo = isDefinedIncludingZero(decimalToValue);
  const decimalFromToValue = (isDecimalFrom && decimalFromValue) || (isDecimalTo && decimalToValue);

  const fieldValue = {
    [FilterType.TEXT]: [value[0].value],
    [FilterType.TEXT_RANGE]: [value[0].value],
    [FilterType.DATE_RANGE]: value && [
      (dateFromValue && dateToValue)
        ? (dateFromValue + ' - ' + dateToValue)
        : (notSpecified
          ? translation.filters_not_specified_title
          : (dateFromValue || dateToValue)
        )
    ],
    [FilterType.DECIMAL_RANGE]: value && [
      (isDecimalFrom && isDecimalTo)
        ? (decimalFromValue + ' - ' + decimalToValue)
        : decimalFromToValue
    ],
    [FilterType.CUSTOM]: [getCustomValue(value, fieldType)],
    [FilterType.EXTERNAL_VALUE]: [getExternalValue(value, fieldType, translation.filters_not_specified_title)]
  } || {};

  return fieldValue[filterType] || values[id].map(v => v.description);
};

const mapCustomField = (dataToMap: MapCustomFieldPayload): FilterSummaryField[] => {
  const { filterType, fieldType, id, label, values = {}, menuValues = {}, translation, locale } = dataToMap;
  return values[id] && values[id].length && isFilterValid(dataToMap)
    ? [{
      id,
      label: label,

      values: maptCustomFieldValues(id, filterType, fieldType, values, translation, locale),

      options: [
        ...(menuValues && menuValues[id] && menuValues[id].exclude ? [translation.filter_summary_exclude] : []),
        ...(menuValues && menuValues[id] && menuValues[id].excludeChildren ? [translation.filter_summary_exclude_sub] : []),
        ...(menuValues && menuValues[id] && menuValues[id].excludeParents ? [translation.filter_summary_exclude_parent] : [])
      ]
    }]
    : [];
};

const isFilterValid = (dataToMap: MapCustomFieldPayload): boolean => {
  const { filterType, id, filterValidation } = dataToMap;
  if (filterType !== FilterType.TEXT_RANGE) {
    return true;
  }

  const validationResult = filterValidation && filterValidation.find(validation => validation.id === id);

  return !(validationResult && validationResult.validation && validationResult.validation.level === ValidationLevel.error);
};

const mapCustomFilters = (dataToMap: MapToSummaryPayload): FilterSummary[] => {
  const { filters = [], values, menuValues, translation = {} as SearchFiltersTranslation, filterValidation, locale } = dataToMap;
  return filters
    .reduce((summary, filter) => {
      const filterFields = filter.fields;

      if (!filterFields) {
        return [];
      }

      const fields = filterFields.filter(field => field.includeInSummary !== false)
        .reduce((result, field) => [
          ...result,
          ...mapCustomField({
            id: getFieldId(filter.id, field.fieldTypeId),
            label: field.label, values, menuValues, translation,
            filterType: filter.type, filterValidation,
            fieldType: field.type,
            locale
          })
        ], []);

      return fields.length > 0
        ? [
          ...summary,
          {
            title: filter.label,
            fields
          }
        ]
        : summary;
    }, [] as FilterSummary[]);

};

const mapToOptionItems = (filters: FilterSummary[]): OptionItem[] =>
  filters.reduce((result, filter) => [
    ...result,
    ...filter.fields.map(field => ({
      label: field.label ? `${filter.title} - ${field.label}` : filter.title,
      value: field.id,
      selections: {
        values: field.values,
        excludedValues: field.options,
        titles: []
      }
    }))
  ], [] as OptionItem[]);

export const mapToSummary = (dataToMap: MapToSummaryPayload): OptionItem[] => {
  const { values = {}, translation = {} as SearchFiltersTranslation } = dataToMap;

  return mapToOptionItems([
    ...mapPredefinedFilters(values[PredefinedIds.include], values[PredefinedIds.exclude], translation),
    ...mapCustomFilters(dataToMap)
  ]);
};
